import { RSAKeyPair } from 'client/PasswordManager/PasswordManagerCryptoUtil';
import { keyBy } from 'lodash';
import PasswordManagerBackupApi from 'client/api/v2/PasswordManagerBackupApi';
import PasswordManagerBackupKeyApi from 'client/api/v2/PasswordManagerBackupKeyApi';
import PasswordManagerFilters from 'client/PasswordManager/PasswordManagerFilters';
import UserApi from 'client/api/v2/UserApi';
import Vue from 'vue';

const CLEAR_ALL = 'CLEAR_ALL';
const SET_ACTIVE_KEYS = 'SET_ACTIVE_KEYS';
const SET_DISABLED_KEYS = 'SET_DISABLED_KEYS';
const SET_DISABLED_KEYS_TOTAL_COUNT = 'SET_DISABLED_KEYS_TOTAL_COUNT';
const SET_GENERATED_KEY_PAIR = 'SET_GENERATED_KEY_PAIR';
const SET_HAS_ERROR_LOADING_ACTIVE_KEYS = 'SET_HAS_ERROR_LOADING_ACTIVE_KEYS';
const SET_HAS_LOADED_ACTIVE_KEYS = 'SET_HAS_LOADED_ACTIVE_KEYS';
const SET_IS_LOADING_ACTIVE_KEYS = 'SET_IS_LOADING_ACTIVE_KEYS';

export const initialState = () => ({
  activeKeys: [],
  disabledKeys: [],
  disabledKeysTotalCount: 0,
  generatedKeyPair: {
    name: '',
    privateKey: '',
    publicKey: '',
  },
  hasErrorLoadingActiveKeys: false,
  hasLoadedActiveKeys: false,
  isLoadingActiveKeys: false,
});

const bindKeysWithAdmins = async (keys) => {
  const uniqAdminIds = keys.map(key => key.createdBy);
  const promises = uniqAdminIds.map(id => UserApi.get(id));
  const promiseResults = await Promise.allSettled(promises);

  /**
   * UserApi.get calls are used to get admin name that has the key
   * we don't want to block the restore in case the request failed
   */
  const admins = promiseResults.reduce((a, c) => (c.status === 'fulfilled' ? a.concat(c.value) : a), []);

  const adminsMap = keyBy(admins, '_id');

  return keys.map((key) => ({
    ...key, createdByAdmin: adminsMap[key.createdBy],
  }));
};

export const createModule = (api = PasswordManagerBackupKeyApi) => ({
  namespaced: true,
  state: initialState(),

  actions: {
    clearCloudBackups({ commit }) {
      commit(CLEAR_ALL);
    },

    clearGeneratedKeyPair({ commit }) {
      commit(SET_GENERATED_KEY_PAIR, initialState().generatedKeyPair);
    },

    async deleteKey({ dispatch }, keyId) {
      await api
        .delete(keyId);

      dispatch('fetchDisabledKeys');
    },

    async disableCloudBackups({ dispatch }) {
      const allKeys = await api.fetchAll();

      const promises = allKeys.map((key => api.delete(key.id)));
      await Promise.all(promises);

      dispatch('fetchActiveKeys');
    },

    async fetchActiveKeys({ commit }) {
      commit(SET_IS_LOADING_ACTIVE_KEYS, true);
      commit(SET_HAS_LOADED_ACTIVE_KEYS, false);
      commit(SET_HAS_ERROR_LOADING_ACTIVE_KEYS, false);
      commit(SET_ACTIVE_KEYS, []);
      try {
        const activeKeys = await api
          .fetchAll(null, null, [PasswordManagerFilters.activeBackupKeys(1)]);

        const activeKeysWithAdmins = await bindKeysWithAdmins(activeKeys);

        commit(SET_ACTIVE_KEYS, activeKeysWithAdmins);
        commit(SET_HAS_LOADED_ACTIVE_KEYS, true);
      } catch (error) {
        commit(SET_HAS_ERROR_LOADING_ACTIVE_KEYS, true);
      } finally {
        commit(SET_IS_LOADING_ACTIVE_KEYS, false);
      }
    },

    async fetchDisabledKeys({ commit }) {
      const disabledKeys = await api
        .fetchAll(null, null, [PasswordManagerFilters.activeBackupKeys(0)]);
      const disabledKeysWithAdmins = await bindKeysWithAdmins(disabledKeys);
      commit(SET_DISABLED_KEYS, disabledKeysWithAdmins);
    },

    async fetchDisabledKeysTotalCount({ commit }) {
      const disabledKeysTotalCount = await api
        .fetchTotalCount([PasswordManagerFilters.activeBackupKeys(0)]);
      commit(SET_DISABLED_KEYS_TOTAL_COUNT, disabledKeysTotalCount);
    },

    fetchBackupsCountByKey(context, backupKeyId) {
      return PasswordManagerBackupApi
        .fetchTotalCount([PasswordManagerFilters.backupKey(backupKeyId)]);
    },

    generateKeyPair({ commit }, keyName) {
      return new Promise((resolve, reject) => {
        try {
          const rsaKeyPair = RSAKeyPair.GenerateKeyPair();

          const publicKey = rsaKeyPair.getPublicKey({ trimHeaderAndFooter: true });
          commit(SET_GENERATED_KEY_PAIR, {
            name: keyName,
            privateKey: rsaKeyPair.getPrivateKey(),
            publicKey,
          });
          resolve();
        } catch (error) {
          reject(error);
        }
      });
    },

    async storeKey({ dispatch, state }) {
      await api
        .create({
          name: state.generatedKeyPair.name,
          publicKey: state.generatedKeyPair.publicKey,
        });
      dispatch('fetchActiveKeys');
    },
  },

  getters: {
    activeKeys(state) {
      return state.activeKeys;
    },

    disabledKeys(state) {
      return state.disabledKeys;
    },

    disabledKeysTotalCount(state) {
      return state.disabledKeysTotalCount;
    },

    generatedKeyPair(state) {
      return state.generatedKeyPair;
    },

    hasErrorLoadingActiveKeys(state) {
      return state.hasErrorLoadingActiveKeys;
    },

    hasLoadedActiveKeys(state) {
      return state.hasLoadedActiveKeys;
    },

    isCloudBackupsEnabled(state) {
      return state.hasLoadedActiveKeys && state.activeKeys.length;
    },

    isLoadingActiveKeys(state) {
      return state.isLoadingActiveKeys;
    },
  },

  mutations: {
    [CLEAR_ALL](state) {
      const initState = initialState();
      Vue.set(state, 'activeKeys', initState.activeKeys);
      Vue.set(state, 'generatedKeyPair', initState.generatedKeyPair);
      Vue.set(state, 'hasErrorLoadingActiveKeys', initState.hasErrorLoadingActiveKeys);
      Vue.set(state, 'hasLoadedActiveKeys', initState.hasLoadedActiveKeys);
      Vue.set(state, 'isLoadingActiveKeys', initState.isLoadingActiveKeys);
    },

    [SET_ACTIVE_KEYS](state, activeKeys) {
      Vue.set(state, 'activeKeys', activeKeys);
    },

    [SET_DISABLED_KEYS](state, disabledKeys) {
      Vue.set(state, 'disabledKeys', disabledKeys);
    },

    [SET_DISABLED_KEYS_TOTAL_COUNT](state, disabledKeysTotalCount) {
      Vue.set(state, 'disabledKeysTotalCount', disabledKeysTotalCount);
    },

    [SET_GENERATED_KEY_PAIR](state, generatedKeyPair) {
      Vue.set(state, 'generatedKeyPair', generatedKeyPair);
    },

    [SET_HAS_ERROR_LOADING_ACTIVE_KEYS](state, hasError) {
      Vue.set(state, 'hasErrorLoadingActiveKeys', hasError);
    },

    [SET_HAS_LOADED_ACTIVE_KEYS](state, hasLoaded) {
      Vue.set(state, 'hasLoadedActiveKeys', hasLoaded);
    },

    [SET_IS_LOADING_ACTIVE_KEYS](state, isLoading) {
      Vue.set(state, 'isLoadingActiveKeys', isLoading);
    },

    [CLEAR_ALL](state) {
      const initState = initialState();
      Vue.set(state, 'activeKeys', initState.activeKeys);
      Vue.set(state, 'hasErrorLoadingActiveKeys', initState.hasErrorLoadingActiveKeys);
      Vue.set(state, 'hasLoadedActiveKeys', initState.hasLoadedActiveKeys);
      Vue.set(state, 'isLoadingActiveKeys', initState.isLoadingActiveKeys);
      Vue.set(state, 'generatedKeyPair', initState.generatedKeyPair);
      Vue.set(state, 'generatedKeyPair', initState.generatedKeyPair);
      Vue.set(state, 'disabledKeys', initState.disabledKeys);
      Vue.set(state, 'disabledKeysTotalCount', initState.disabledKeysTotalCount);
    },
  },
});

export default createModule();
